import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { endpoints } from '../config/apiConfig';

const LoginUsuario = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [sistemaDisponible, setSistemaDisponible] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rol: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const obtenerRoles = async () => {
      try {
        const respuesta = await fetch(endpoints.roles);
        if (!respuesta.ok) {
          throw new Error('Error en la respuesta del servidor');
        }
        const datos = await respuesta.json();
        
        // Verificar la estructura de la respuesta
        if (Array.isArray(datos)) {
          setRoles(datos);
        } else if (datos.roles && Array.isArray(datos.roles)) {
          setRoles(datos.roles);
        } else {
          throw new Error('Formato de respuesta inválido');
        }
      } catch (error) {
        console.error('Error al cargar roles:', error);
        setError('Error al cargar los tipos de usuario');
      }
    };

    const verificarEstadoSistema = async () => {
      try {
        const response = await fetch(endpoints.estadoSistema);
        const data = await response.json();

        if (!data.sistema_disponible) {
          setSistemaDisponible(false);
          toast.error(
            <div>
              <p className="font-bold">{data.mensaje}</p>
              <p className="text-sm mt-2">Tiempo restante: {data.tiempo_restante}</p>
              <p className="text-sm">Disponible hasta: {data.hora_fin}</p>
            </div>,
            {
              position: "bottom-right",
              autoClose: false,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: false,
            }
          );
        }
      } catch (error) {
        console.error('Error al verificar estado del sistema:', error);
      }
    };

    verificarEstadoSistema();
    obtenerRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.rol) {
      setError('Todos los campos son obligatorios');
      return false;
    }
    if (!/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      setError('Por favor ingrese un correo electrónico válido');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!sistemaDisponible) {
      setError('El sistema no está disponible en este momento');
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Primer paso: Login
      const loginResponse = await fetch(endpoints.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          rol: parseInt(formData.rol)
        })
      });

      const loginData = await loginResponse.json();

      if (loginResponse.ok) {
        const token = loginData.token;
        localStorage.setItem('token', token);

        // Segundo paso: Obtener información del usuario
        const userInfoResponse = await fetch(endpoints.userInfo, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        const userInfoData = await userInfoResponse.json();

        if (userInfoData.ok) {
          localStorage.setItem('userEmail', formData.email);
          localStorage.setItem('userRole', formData.rol);
          localStorage.setItem('programaId', userInfoData.user.programa_id);
          localStorage.setItem('userName', userInfoData.user.nombre);
          localStorage.setItem('userDocument', userInfoData.user.cedula);

          toast.success('Ha ingresado exitosamente', {
            position: 'bottom-right',
            autoClose: 2000
          });
          
          // Redirect based on user role
          if (formData.rol === '1') {
            navigate('/dashboard-admin');
          } else {
            navigate('/listado-estudiantes');
          }
        } else {
          throw new Error('Error al obtener información del usuario');
        }
      } else {
        setError(loginData.message || 'Credenciales inválidas');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error al conectar con el servidor. Por favor, intente más tarde.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img 
            src="http://sievi.udi.edu.co/sievi/img/logo-udi-negro.bbe5273e.png" 
            alt="Logoo UDI" 
            className="h-14 w-auto"
          />
        </div>
        <h1 className="text-2xl font-bold text-center text-gray-800 mb-8">
          Revisión de Matrícula
        </h1>
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Correo electrónico
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                !sistemaDisponible ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="Ingrese su email"
              disabled={loading || !sistemaDisponible}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Contraseña
            </label>
            <div className="relative">
              <input
                type={mostrarPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  !sistemaDisponible ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="Ingrese su contraseña"
                disabled={loading || !sistemaDisponible}
              />
              <button
                type="button"
                onClick={() => setMostrarPassword(!mostrarPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {mostrarPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="rol" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Tipo de Usuario
            </label>
            <select
              id="rol"
              name="rol"
              value={formData.rol}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                !sistemaDisponible ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              disabled={loading || !sistemaDisponible}
            >
              <option value="">Seleccione el tipo de Usuario</option>
              {roles.map(rol => (
                <option key={rol.id} value={rol.id}>
                  {rol.nombre}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading || !sistemaDisponible}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${
              loading || !sistemaDisponible
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-neutral-900 hover:bg-neutral-800'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Verificando...
              </div>
            ) : (
              sistemaDisponible ? 'Ingresar' : 'Sistema en Mantenimiento'
            )}
          </button>
        </form>
        <div className="text-center mt-4">
          <a
            href={process.env.PUBLIC_URL + '/INSTRUCTIVO_REVISIÓN_DE_MATRICULA_DIRECTORES_DE_PROGRAMA.pdf'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline text-sm"
          >
            Ver Instructivo
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginUsuario;
